import type { FC, ReactNode } from 'react'

import { useState } from 'react'
import Image from 'next/image'
import { ArrowUpRight, Plus } from 'lucide-react'

import { cn } from '@/lib/utils'
import { contactDetails } from '@/constants/site'
import { faqItems, faqAuthor } from '@/constants/contact'

/**
 * Split heading, author card and accordion column, as on the original's book
 * page. Rows open one at a time.
 */
export const Faq: FC = (): ReactNode => {
    const [openIndex, setOpenIndex] = useState<number | null>(null)

    return (
        <section className='shell grid gap-12 py-20 md:py-24 lg:grid-cols-[0.85fr_1.15fr] lg:gap-16 lg:py-28'>
            <div>
                <h2
                    className='font-heading text-[86px] font-medium leading-[0.9] tracking-[-0.04em] text-brand md:text-[110px]'
                    data-reveal='up'
                >
                    FAQ&rsquo;s
                </h2>

                <p className='mt-4 max-w-[330px] text-[16px] leading-[1.5] text-ink-soft' data-reveal='up'>
                    Answers to the most common questions about staying at the Greenbrier Inn.
                </p>

                <div
                    className='mt-10 max-w-[360px] rounded-[10px] border border-hairline bg-cream-soft p-6'
                    data-reveal='up'
                >
                    <div className='flex items-center gap-3'>
                        <span className='relative size-11 overflow-hidden rounded-full bg-cream-deep'>
                            <Image
                                src={faqAuthor.image}
                                alt=''
                                fill
                                sizes='44px'
                                className='object-cover'
                            />
                        </span>

                        <span>
                            <span className='block text-[17px] font-medium leading-tight text-ink'>
                                {faqAuthor.name}
                            </span>

                            <span className='block text-[14px] leading-tight text-ink-soft'>
                                {faqAuthor.role}
                            </span>
                        </span>
                    </div>

                    <p className='mt-5 text-[15px] leading-[1.6] text-ink-soft'>
                        {faqAuthor.note}
                    </p>

                    <div className='mt-6 flex items-center gap-2'>
                        <a
                            href={contactDetails.phoneHref}
                            className='rounded-full bg-brand px-6 py-3.5 text-[16px] font-medium text-cream transition-colors duration-300 hover:bg-ink'
                        >
                            Call {contactDetails.phone}
                        </a>

                        <a
                            href={contactDetails.phoneHref}
                            aria-label='Call the front desk'
                            className='group flex size-[50px] shrink-0 items-center justify-center rounded-full bg-brand text-cream transition-colors duration-300 hover:bg-ink'
                        >
                            <ArrowUpRight className='size-5 transition-transform duration-300 group-hover:rotate-45' />
                        </a>
                    </div>
                </div>
            </div>

            <ul className='flex flex-col gap-2.5' data-reveal-group data-reveal-stagger='0.08'>
                {faqItems.map((item, index) => {
                    const open = openIndex === index

                    return (
                        <li
                            key={item.question}
                            className='overflow-hidden rounded-[10px] border border-hairline bg-cream-soft'
                            data-reveal='up'
                        >
                            <button
                                type='button'
                                onClick={() => setOpenIndex(open ? null : index)}
                                aria-expanded={open}
                                className='flex w-full items-center justify-between gap-5 px-6 py-5 text-left'
                            >
                                <span className='font-heading text-[19px] font-medium leading-[1.25] text-ink md:text-[21px]'>
                                    {item.question}
                                </span>

                                <span className='flex size-7 shrink-0 items-center justify-center rounded-full bg-brand text-cream'>
                                    <Plus
                                        className={cn(
                                            'size-4 transition-transform duration-300',
                                            open && 'rotate-45'
                                        )}
                                    />
                                </span>
                            </button>

                            <div
                                className={cn(
                                    'grid transition-[grid-template-rows] duration-500 ease-out',
                                    open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                                )}
                            >
                                <div className='overflow-hidden'>
                                    <p className='px-6 pb-6 text-[15px] leading-[1.65] text-ink-soft'>
                                        {item.answer}
                                    </p>
                                </div>
                            </div>
                        </li>
                    )
                })}
            </ul>
        </section>
    )
}
